import type { CSSProperties } from "react";
import type { FinishStep } from "../types/drink";
import { finishPrefix, scaleFinish } from "../lib/scaling";

interface Props {
  finishSteps: FinishStep[];
  cocktailCount: number;
  /** Map of recipe_finish_step.id → checked. Defaults to empty (unchecked). */
  checked: Record<string, boolean>;
  onToggle: (finishStepId: string) => void;
}

/**
 * Bartender Finish / Presentation tap-checkoff list.
 *
 * Mirrors the IngredientList pattern (full-row tap target, big check, line-through
 * when complete) but renders rows differently per scale_type:
 *   per_cocktail  — multiplied label ("3× Express orange peel")
 *   do_not_scale  — singular regardless of count
 *   optional      — same as per_cocktail but rendered de-emphasized + "Optional" pill
 *   batch_finish  — singular with "(once for the round)" hint
 *
 * If a step has a quantity (e.g. "0.5 oz Cream Top"), it's rendered as a small
 * meta line below the label. Quantity scaling follows the cocktail-count rule
 * for per_cocktail / optional only.
 */
export default function FinishStepsList({
  finishSteps,
  cocktailCount,
  checked,
  onToggle,
}: Props) {
  if (finishSteps.length === 0) return null;
  return (
    <ul style={styles.list}>
      {finishSteps.map((step) => {
        const isChecked = !!checked[step.id];
        const scaled = scaleFinish(step.scale_type, cocktailCount);
        const prefix = finishPrefix(scaled);

        // Quantity scaling: only multiply for per_cocktail / optional. Otherwise singular.
        const qty =
          step.quantity != null
            ? scaled.doNotScale || scaled.batch
              ? step.quantity
              : step.quantity * scaled.multiplier
            : null;

        return (
          <li key={step.id}>
            <button
              type="button"
              onClick={() => onToggle(step.id)}
              aria-pressed={isChecked}
              style={{
                ...styles.row,
                ...(isChecked ? styles.rowChecked : {}),
                ...(scaled.optional && !isChecked ? styles.rowOptional : {}),
              }}
            >
              <span
                style={{
                  ...styles.checkCircle,
                  ...(isChecked ? styles.checkCircleActive : {}),
                }}
                aria-hidden
              >
                {isChecked ? "✓" : ""}
              </span>
              <div style={styles.body}>
                <div style={styles.headRow}>
                  <span
                    style={{
                      ...styles.label,
                      ...(isChecked ? styles.labelChecked : {}),
                    }}
                  >
                    {prefix}
                    {step.label}
                  </span>
                  <FinishMeta step={step} scaled={scaled} />
                </div>
                {step.instruction && (
                  <p style={styles.instruction}>{step.instruction}</p>
                )}
                {qty != null && (
                  <p style={styles.qtyLine}>
                    {formatQty(qty)} {step.unit ?? ""}
                  </p>
                )}
                {scaled.batch && (
                  <p style={styles.hint}>Once for the round.</p>
                )}
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function FinishMeta({
  step,
  scaled,
}: {
  step: FinishStep;
  scaled: ReturnType<typeof scaleFinish>;
}) {
  return (
    <span style={styles.metaChips}>
      <span style={styles.typeChip}>{step.finish_type}</span>
      {scaled.optional && <span style={styles.optionalChip}>Optional</span>}
      {scaled.doNotScale && <span style={styles.doNotScaleChip}>×1</span>}
      {scaled.batch && <span style={styles.batchChip}>Batch</span>}
    </span>
  );
}

function formatQty(qty: number): string {
  return qty
    .toFixed(2)
    .replace(/\.00$/, "")
    .replace(/(\.\d)0$/, "$1");
}

const styles: Record<string, CSSProperties> = {
  list: { listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 4 },
  row: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "32px 1fr",
    columnGap: 10,
    alignItems: "flex-start",
    minHeight: 56,
    padding: "10px 6px",
    border: 0,
    borderBottom: "1px solid var(--border)",
    background: "transparent",
    color: "var(--text)",
    cursor: "pointer",
    textAlign: "left",
    WebkitTapHighlightColor: "transparent",
  },
  rowChecked: {
    opacity: 0.55,
    background: "var(--bg)",
  },
  rowOptional: {
    background: "rgba(176,160,140,0.06)",
  },
  checkCircle: {
    width: 24,
    height: 24,
    borderRadius: 999,
    border: "2px solid var(--border)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 900,
    background: "var(--surface)",
    color: "var(--text)",
    marginTop: 2,
  },
  checkCircleActive: {
    background: "var(--gold)",
    borderColor: "var(--gold)",
    color: "#0D0B08",
  },
  body: { display: "grid", gap: 4 },
  headRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "baseline",
    justifyContent: "space-between",
  },
  label: {
    fontSize: 15,
    fontWeight: 800,
    color: "var(--text)",
    lineHeight: 1.25,
  },
  labelChecked: {
    textDecoration: "line-through",
  },
  metaChips: {
    display: "inline-flex",
    flexWrap: "wrap",
    gap: 4,
    alignItems: "center",
  },
  typeChip: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--muted)",
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 999,
    padding: "2px 7px",
  },
  optionalChip: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "#93c5fd",
    background: "rgba(96,165,250,0.12)",
    border: "1px solid rgba(96,165,250,0.3)",
    borderRadius: 999,
    padding: "2px 7px",
  },
  doNotScaleChip: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 0.4,
    color: "var(--muted)",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 999,
    padding: "2px 7px",
  },
  batchChip: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--gold)",
    background: "rgba(249,175,54,0.12)",
    border: "1px solid rgba(249,175,54,0.3)",
    borderRadius: 999,
    padding: "2px 7px",
  },
  instruction: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.35,
    color: "var(--muted)",
  },
  qtyLine: {
    margin: 0,
    fontSize: 12,
    fontWeight: 700,
    color: "var(--text)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  hint: {
    margin: 0,
    fontSize: 11,
    fontStyle: "italic",
    color: "var(--gold)",
  },
};
